import { Permission } from '../enums';
import { IPermission, IPermissionGroup } from '../models';
import { SelectOption } from '../models';

export const PERMISSIONS: IPermission[] = [
  { key: Permission.USERS_VIEW, desc: 'See the Users, Groups and Roles admin pages and all user details' },
  { key: Permission.USERS_MANAGE, desc: 'Create and Manage Users, Roles and Groups' },
  { key: Permission.ALERTS_VIEW, desc: 'See the Alerts admin page and all alert details' },
  { key: Permission.ALERTS_MANAGE, desc: 'Create and Manage existing alerts' },
  { key: Permission.AUDIT_LOG_VIEW, desc: 'See the Audit Log page and all audit log details' },
  { key: Permission.TRANSACTIONS_VIEW, desc: 'See the Transactions page and all process details' },
  { key: Permission.TRANSACTIONS_MANAGE, desc: 'Create and manage existing transactions' },
  { key: Permission.DATA_COLLECTION_VIEW, desc: 'See the Data Collection page and all data collection details' },
  { key: Permission.WAREHOUSE_VIEW, desc: 'See the Warehouse list and all Warehouse details' },
  { key: Permission.WAREHOUSE_MANAGE, desc: 'Create and manage existing Warehouses' },
  { key: Permission.INVENTORY_VIEW, desc: 'See the Stock Visibility page and all inventory details' },
  { key: Permission.INVENTORY_MANAGE, desc: 'Create and manage existing inventory' },
  { key: Permission.FORECAST_VIEW, desc: 'See the Forecast Reconciliation page and all forecast details' },
  { key: Permission.FORECAST_MANAGE, desc: 'Create and manage existing forecast reconciliations' },
  { key: Permission.INTEGRATIONS_VIEW, desc: 'Access and view Integrations Page / API keys' },
  { key: Permission.INTEGRATIONS_MANAGE, desc: 'Manage API Keys' },
  { key: Permission.ADMIN, desc: 'System admin role for full access' },
  { key: Permission.SUPPLY_NETWORK_VIEW, desc: 'See the Order Management page and all order details' },
  { key: Permission.SUPPLY_NETWORK_MANAGE, desc: 'Create and manage existing orders' },
  {
    key: Permission.SUPPLIER_PERFORMANCE_VIEW,
    desc: 'See the Supplier Performance page and all supplier performance details'
  },
  { key: Permission.SHIPPING_VIEW, desc: 'See the Shipping page and all shipping details' },
  { key: Permission.SHIPPING_MANAGE, desc: 'Create and manage existing shipping' },
];


export const PERMISSION_SELECTIONS: IPermissionGroup[] = [
  {
    key: 'transactions',
    title: 'Transactions',
    desc: 'Access to transaction monitor',
    view: [Permission.TRANSACTIONS_VIEW],
    hasManage: true,
    manage: [Permission.TRANSACTIONS_MANAGE],
  },
  {
    key: 'data-collection',
    title: 'Data Collection',
    desc: 'Access to the data collection pages. Some permissions are defined by Warehouse assignment',
    view: [Permission.DATA_COLLECTION_VIEW],
    hasManage: false,
  },
  {
    key: 'warehouses',
    title: 'Warehouses',
    desc: 'Access to Warehouse management and Templates',
    view: [Permission.WAREHOUSE_VIEW],
    hasManage: true,
    manage: [Permission.WAREHOUSE_MANAGE],
  },
  {
    key: 'alerts',
    title: 'Alerts',
    desc: 'Access to the Alerts admin page',
    view: [Permission.ALERTS_VIEW],
    hasManage: true,
    manage: [Permission.ALERTS_MANAGE],
  },
  {
    key: 'audit-logs',
    title: 'Audit Logs',
    desc: 'Access to audit logs',
    view: [Permission.AUDIT_LOG_VIEW],
    hasManage: false,
  },
  {
    key: 'users',
    title: 'Users',
    desc: 'Access to users, groups and roles',
    view: [Permission.USERS_VIEW],
    hasManage: true,
    manage: [Permission.USERS_MANAGE],
  },
  {
    key: 'integrations',
    title: 'Integrations',
    desc: 'Access to integration options such as API keys',
    view: [Permission.INTEGRATIONS_VIEW],
    hasManage: true,
    manage: [Permission.INTEGRATIONS_MANAGE],
  },
  {
    key: 'inventory',
    title: 'Inventory',
    desc: 'Access to the Inventory module',
    view: [Permission.INVENTORY_VIEW],
    hasManage: true,
    manage: [Permission.INVENTORY_MANAGE],
  },
  {
    key: 'forecast',
    title: 'Forecast',
    desc: 'Access to the Forecast module',
    view: [Permission.FORECAST_VIEW],
    hasManage: true,
    manage: [Permission.FORECAST_MANAGE],
  },
  {
    key: 'supply-network',
    title: 'Order Management',
    desc: 'Access to the Order Management module',
    view: [Permission.SUPPLY_NETWORK_VIEW],
    hasManage: true,
    manage: [Permission.SUPPLY_NETWORK_MANAGE],
  },
  {
    key: 'supplier-performance',
    title: 'Supplier Performance',
    desc: 'Access to the Supplier Performance module',
    view: [Permission.SUPPLIER_PERFORMANCE_VIEW],
    hasManage: false,
  },
  {
    key: 'shipping',
    title: 'Shipping',
    desc: 'Access to the Shipment Management module',
    view: [Permission.SHIPPING_VIEW],
    hasManage: true,
    manage: [Permission.SHIPPING_MANAGE],
  },
];

export const RolePermissions: SelectOption<Permission>[] = [
  {
    key: Permission.USERS_VIEW,
    value: 'User Management : View',
    desc: 'See the Users, Groups and Roles admin pages and all user details',
  },
  {
    key: Permission.USERS_MANAGE,
    value: 'User Management : Manage',
    desc: 'Create and Manage Users, Roles and Groups',
  },
  {
    key: Permission.ALERTS_MANAGE,
    value: 'Alerts : Manage',
    desc: 'Create and Manage existing alerts',
  },
  {
    key: Permission.ALERTS_VIEW,
    value: 'Alerts : View',
    desc: 'See the Alerts admin page and all alert details',
  },
  {
    key: Permission.AUDIT_LOG_VIEW,
    value: 'Audit Log : View',
    desc: 'See the Audit Log page and all audit log details',
  },
  {
    key: Permission.DATA_COLLECTION_VIEW,
    value: 'Data Collection : View',
    desc: 'See the Data Collection page and all data collection details',
  },
  {
    key: Permission.TRANSACTIONS_VIEW,
    value: 'Transactions : View',
    desc: 'See the Transactions page and all process details',
  },
  {
    key: Permission.TRANSACTIONS_MANAGE,
    value: 'Transactions : Manage',
    desc: 'Create and manage existing transactions',
  },
  {
    key: Permission.WAREHOUSE_VIEW,
    value: 'Warehouse : View',
    desc: 'See the Warehouse list and all Warehouse details',
  },
  {
    key: Permission.WAREHOUSE_MANAGE,
    value: 'Warehouse : Manage',
    desc: 'Create and manage existing Warehouses',
  },
  {
    key: Permission.INVENTORY_VIEW,
    value: 'Inventory : View',
    desc: 'See the Stock Visibility page and all inventory details',
  },
  {
    key: Permission.INVENTORY_MANAGE,
    value: 'Inventory : Manage',
    desc: 'Create and manage existing inventory',
  },
  {
    key: Permission.FORECAST_VIEW,
    value: 'Forecast : View',
    desc: 'See the Forecast Reconciliation page and all forecast details',
  },
  {
    key: Permission.FORECAST_MANAGE,
    value: 'Forecast : Manage',
    desc: 'Create and manage existing forecast reconciliations',
  },
  {
    key: Permission.INTEGRATIONS_VIEW,
    value: 'Integrations : View',
    desc: 'Access and view Integrations Page / API keys',
  },
  {
    key: Permission.INTEGRATIONS_MANAGE,
    value: 'Integrations : Manage',
    desc: 'Manage API Keys',
  },
  {
    key: Permission.SUPPLY_NETWORK_MANAGE,
    value: 'Order Management : Manage',
    desc: 'Create and manage existing orders',
  },
  {
    key: Permission.SUPPLY_NETWORK_VIEW,
    value: 'Order Management : View',
    desc: 'See the Order Management page and all order details',
  },
  {
    key: Permission.SUPPLIER_PERFORMANCE_VIEW,
    value: 'Supplier Performance : View',
    desc: 'See the Supplier Performance page and all supplier performance details',
  },
  {
    key: Permission.SHIPPING_VIEW,
    value: 'Shipping : View',
    desc: 'See the Shipment Management page and all shipment details',
  },
  {
    key: Permission.SHIPPING_MANAGE,
    value: 'Shipping : Manage',
    desc: 'Create and manage existing shipping',
  },
];
